import type { CreateBannerDto } from "@/dtos/CreateBannerDto";
import type { UpdateBannerDto } from "@/dtos/UpdateBannerDto";
import type { BannerType } from "@/types/Banner";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

interface GetBannersParams {
  page?: number;
  limit?: number;
  type?: BannerType | "all";
  search?: string;
}

export const useGetBanners = (params?: GetBannersParams) => {
  const page = params?.page || 1;
  const limit = params?.limit || 10;
  const type = params?.type;
  const search = params?.search;

  return useQuery({
    queryKey: ["getBanners", page, limit, type, search],
    queryFn: async () => {
      const response = await api.get("/banners", {
        params: {
          page,
          limit,
          ...(type && type !== "all" && { type }),
          ...(search && { search }),
        },
      });

      return response.data;
    },
  });
};

export const useGetBannerById = (id: string) => {
  return useQuery({
    queryKey: ["getBannerById", id],
    queryFn: async () => {
      const response = await api.get(`/banners/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateBanner = () => {
  return useMutation({
    mutationKey: ["createBanner"],
    mutationFn: async (createBannerData: CreateBannerDto) => {
      const response = await api.post("/banners", createBannerData);
      return response.data;
    },
  });
};

export const useUpdateBanner = () => {
  return useMutation({
    mutationKey: ["updateBanner"],
    mutationFn: async (updateBannerData: UpdateBannerDto) => {
      const { id, ...data } = updateBannerData;
      const response = await api.patch(`/banners/${id}`, data);
      return response.data;
    },
  });
};

export const useToggleBannerActive = () => {
  return useMutation({
    mutationKey: ["toggleBannerActive"],
    mutationFn: async (id: string) => {
      const response = await api.patch(`/banners/${id}/toggle-active`);
      return response.data;
    },
  });
};

export const useReorderBanners = () => {
  return useMutation({
    mutationKey: ["reorderBanners"],
    mutationFn: async (items: { id: string; order: number }[]) => {
      const response = await api.patch("/banners/reorder", { items });
      return response.data;
    },
  });
};

export const useDeleteBanner = () => {
  return useMutation({
    mutationKey: ["deleteBanner"],
    mutationFn: async (id: string) => {
      const response = await api.delete(`/banners/${id}`);
      return response.data;
    },
  });
};
